import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  useMemo,
} from "react";
import {
  Routes,
  Route,
  Navigate,
  Outlet,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { useHotkeys } from "react-hotkeys-hook";
import Intercom, { shutdown } from "@intercom/messenger-js-sdk";

import "./css/style.css";

import "./charts/ChartjsConfig";
import { useThemeProvider } from "./utils/ThemeContext";

// Import pages
import Dashboard from "./pages/Dashboard";
import Orders from "./pages/orders";
import PageNotFound from "./pages/utility/PageNotFound";
import Signin from "./pages/Signin";
import Signup from "./pages/Signup";
import AccountSetup from "./pages/onboarding/AccountSetup.jsx";
import MarketplaceOnboarding from "./pages/onboarding/MarketplaceOnboarding.jsx";
import { addToast } from "./store/app/action.js";
import { useDispatch, useSelector } from "react-redux";
import Sidebar from "./partials/Sidebar.jsx";
import Header from "./partials/Header.jsx";
import { getDashboardActions, getMerchants, login, logout, fetchOnboardSteps, setSelectedMerchant } from "./store/app/action.js";
import { loadCrmSettings } from "./store/crmSettings/action.js";
import LocationSelector from "./pages/component/LocationSelector.jsx";
import { io } from "socket.io-client";
import { getActiveOrders } from "./store/orders/action.js";
import { manageAPI } from "./api/index.js";
import {
  registerNotifications,
  initializeNotificationSettings,
} from "./utils/appNotifications.js";
import Account from "./pages/settings/Account.jsx";
import Payouts from "./pages/settings/Payouts.jsx";
import ProductsListing from "./pages/products/index.jsx";
import MasterMenuManagement from "./pages/products/MasterMenuManagement.jsx";
import MasterMenuCreateProduct from "./pages/products/MasterMenuCreateProduct.jsx";
import Toast from "./components/Toast.jsx";
import Customers from "./pages/ecommerce/Customers.jsx";
import AllStores from "./pages/adminStores/index.jsx";
import config from "@/config";
import ChangePasswordModal from "./pages/component/ChangePasswordModal.jsx";
import Store from "./pages/settings/Store.jsx";
import Business from "./pages/settings/Business.jsx";
import StoreHours from "./pages/settings/StoreHours.jsx";
import OnlineStore from "./pages/settings/OnlineStore.jsx";
import CommissionsSettings from "./pages/settings/CommissionsSettings.jsx";
import LicensesSettings from "./pages/settings/LicensesSettings.jsx";
import PastOrders from "./pages/ecommerce/PastOrders.jsx";
import Marketing from "./pages/Marketing.jsx";
import Users from "./pages/settings/Users.jsx";
import FraudManagement from "./pages/settings/FraudManagement.jsx";
import CanView from "./components/CanView.jsx";
import Notifications from "./pages/settings/Notifications.jsx";
import CustomProduct from "./pages/products/customProduct.jsx";
import Categories from "./pages/categories/index.jsx";
import Merchants from "./pages/merchants/index.jsx";
import Cartwheel from "./pages/cartwheel/index.jsx";
import StateRestrictions from "./pages/stateRestrictions/index.jsx";
import MainMenuCards from "./pages/menuCards/index.jsx";
import Billing from "./pages/settings/Billing.jsx";
import TosModal from "./components/TosModal.jsx";
import AnnouncementModal from "./components/AnnouncementModal.jsx";
import MFARequiredModal from "./components/MFARequiredModal.jsx";
import DemoEnvironmentModal from "./components/DemoEnvironmentModal.jsx";
import ProspectConversionModal from "./components/ProspectConversionModal.jsx";
import Customization from "./pages/settings/Customization.jsx";
import IDScans from "./pages/idScans/index.jsx";
import ReviewCustomProducts from "./pages/admin/ReviewCustomProducts.jsx";
import AdminUsers from "./pages/settings/AdminUsers.jsx";
import Mothership from "./pages/settings/Mothership.jsx";
import Reports from "./pages/reports/index.jsx";
import CrmCampaigns from "./pages/crm/Campaign/campaigns.jsx";
import CampaignFormWrapper from "./pages/crm/Campaign/CampaignFormWrapper.jsx";
import ExternalFeedsSettings from "./pages/externalFeeds/settings/index.jsx";
import IngestionDetail from "./pages/externalFeeds/IngestionDetail.jsx";
import CloverCallback from "./pages/externalFeeds/settings/sections/POSConnection/connect/CloverCallback.jsx";
import CrmBroadcasts from "./pages/crm/Broadcast/broadcast.jsx";
import BroadcastFormWrapper from "./pages/crm/Broadcast/BroadcastFormWrapper.jsx";
import CrmSettings from "./pages/settings/CrmSettings.jsx";
import CustomerDetail from "./pages/crm/people/index.jsx";
import MarketplaceCommissions from "./pages/commissions/index.jsx";
import ComplianceLayout from "./pages/taxRules/index.jsx";
import TaxRulesTab from "./pages/taxRules/TaxRulesTab.jsx";
import ComplianceStateRestrictions from "./pages/taxRules/StateRestrictions.jsx";
import CompliancePurchaseAttestations from "./pages/taxRules/PurchaseAttestationsTable.jsx";
import ComplianceProductTags from "./pages/taxRules/ProductTagsTable.jsx";
import ComplianceProductGeorestrictions from "./pages/taxRules/ProductGeorestrictionsTable.jsx";
import ComplianceCheck from "./pages/taxRules/ComplianceCheck.jsx";
import ImpersonationModal from "./components/ImpersonationModal.jsx";
import InlineLoader from "./components/InlineLoader.jsx";
import HotkeysModal from "./components/HotkeysModal.jsx";
import ModalBasic from "./components/ModalBasic.jsx";
import { SafeArea } from "capacitor-plugin-safe-area";
import { App as CapacitorApp } from "@capacitor/app";
import OnboardChecklist from "./components/OnboardChecklist.jsx";
import MarketplaceOnboardingChecklist from "./components/MarketplaceOnboardingChecklist.jsx";
import Tutorial from "react-joyride";
import { EVENTS, ACTIONS, STATUS } from "react-joyride";
let disableStyle;

function disableSelection() {
  if (!disableStyle) {
    disableStyle = document.createElement("style");
    disableStyle.innerHTML = `
      * {
        -webkit-user-select: none !important;
        -ms-user-select: none !important;
        user-select: none !important;
        -webkit-touch-callout: none !important;
      }
    `;
    document.head.appendChild(disableStyle);
  }
}

function enableSelection() {
  if (disableStyle) {
    disableStyle.remove();
    disableStyle = null;
  }
}

document.addEventListener("touchstart", disableSelection, { passive: true });
document.addEventListener("touchend", enableSelection, { passive: true });
document.addEventListener("touchcancel", enableSelection, { passive: true });
function App() {
  const location = useLocation();
  const navigate = useNavigate();
  const resetToken = new URLSearchParams(location.search).get("reset");
  const impersonateID = new URLSearchParams(location.search).get("impersonate");
  const activateHash = new URLSearchParams(location.search).get("activate");
  const dispatch = useDispatch();
  const { isMP, isApp, isDirect } = useThemeProvider();

  const scrollYRef = useCallback(
    (node) => {
      dispatch({ type: "SET_MAIN_CONTAINER", payload: node });
    },
    [dispatch]
  );
  const mainScrollContainerRef = useCallback(
    (node) => {
      dispatch({ type: "SET_MAIN_SCROLL_CONTAINER", payload: node });
    },
    [dispatch]
  );
  const {
    isAuthenticated,
    selectedLocation,
    selectedMerchant,
    toasts,
    user,
    merchants,
    merchantsLoadState,
    mfaSetupRequired,
    showDemoModal,
    prospect_conversion_id,
  } = useSelector((state) => state.app);
  const storedSidebarExpanded = localStorage.getItem("sidebar-expanded");
  const [sidebarExpanded, setSidebarExpanded] = useState(
    storedSidebarExpanded === null ? false : storedSidebarExpanded === "true"
  );

  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Poll prospect conversion job status when prospect_conversion_id is set
  useEffect(() => {
    if (!prospect_conversion_id) return;

    navigate("/onboarding", { replace: true });
    const pollConversionJob = async () => {
      try {
        await manageAPI.get(
          `prospect/conversion-job/${prospect_conversion_id}`
        );
      } catch (error) { }
    };

    // Poll immediately, then every 10 seconds
    pollConversionJob();
    const intervalId = setInterval(pollConversionJob, 10000);

    return () => {
      clearInterval(intervalId);
    };
  }, [prospect_conversion_id, dispatch]);
  const joyrideHelpers = useRef(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  // Debug logging
  const steps = useMemo(
    () =>
      [
        {
          target: ".__tutorial-user-menu",
          disableBeacon: true,
          disableOverlayClose: true,
          hideCloseButton: true,
          hideFooter: true,
          spotlightClicks: true,
          content:
            "This is where you can manage your login.  If you have multiple locations, you can switch between them here.",
        },
        {
          target: ".__tutorial-user-menu",
          disableBeacon: true,
          disableOverlayClose: true,
          hideCloseButton: true,
          hideFooter: true,
          spotlightClicks: true,
          content: "Click again to close.",
        },
        windowWidth < 1536 && {
          disableBeacon: true,
          disableOverlayClose: true,
          hideCloseButton: true,
          hideFooter: true,
          spotlightClicks: true,
          target:
            windowWidth < 1024
              ? ".__tutorial-hamburger-button"
              : ".__tutorial-sidebar-collapse-button",
          content:
            windowWidth < 1024
              ? "This is the hamburger button for the sidebar.  Click it to open the sidebar."
              : "This is the sidebar collapse button.  Click it to collapse the sidebar.",
        },
        {
          target: ".__tutorial-orders-sidebar-links",
          content:
            "This is the orders section.  You can view your active orders and past orders here",
          disableBeacon: true,
          disableOverlayClose: true,
          hideCloseButton: true,
          spotlightClicks: true,
        },
        {
          target: ".__tutorial-catalog-sidebar-links",
          content:
            "This is the catalog section.  You can setup your products and categories here",
          disableBeacon: true,
          disableOverlayClose: true,
          hideCloseButton: true,
          spotlightClicks: true,
        },
        {
          content: (
            <div>
              <h2>Let's all folks</h2>
              <p>
                I'll continue to guide you through the app as you navigate
                around. Start exploring!
              </p>
            </div>
          ),
          placement: "center",
          target: "body",
        },
      ].filter((step) => Boolean(step)),

    [windowWidth]
  );

  const handleJoyrideCallback = (data) => {
    const { action, index, origin, status, type } = data;

    if ([EVENTS.STEP_AFTER, EVENTS.TARGET_NOT_FOUND].includes(type)) {
      // Update state to advance the tour
      setStepIndex(index + (action === ACTIONS.PREV ? -1 : 1));
    } else if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
      // You need to set our running state to false, so we can restart if we click start again.
      setRun(false);
    }
  };
  const [run, setRun] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  useEffect(() => {
    if (impersonateID && merchants?.length > 0) {
      const merch = merchants.find((merchant) =>
        merchant.locations.find(
          (location) => location.store_id === impersonateID
        )
      );
      const loc = merch?.locations.find(
        (location) => location.store_id === impersonateID
      );
      if (loc && merch) {
        window.history.replaceState(
          {},
          document.title,
          window.location.pathname
        );
        dispatch(setSelectedMerchant(merch.id));
        dispatch({ type: "SET_SELECTED_LOCATION", payload: loc.id });
      }
    }
  }, [impersonateID, merchants]);

  // Auto-login effect for activate parameter
  useEffect(() => {
    if (activateHash) {
      try {
        // Logout current user first
        dispatch(logout(true));

        // URL decode the hash
        const urlDecodedHash = decodeURIComponent(activateHash);

        // Base64 decode the hash
        const base64DecodedHash = atob(urlDecodedHash);

        // Parse email::password format
        const [email, password] = base64DecodedHash.split("::");

        if (email && password) {
          // Perform auto-login
          dispatch(login(email, password))
            .then(() => {
              // Clean up URL parameters after successful login
              window.history.replaceState(
                {},
                document.title,
                window.location.pathname
              );
            })
            .catch((error) => {
              // Clean up URL parameters even on failure
              window.history.replaceState(
                {},
                document.title,
                window.location.pathname
              );
            });
        } else {
          // Clean up URL parameters on invalid format
          window.history.replaceState(
            {},
            document.title,
            window.location.pathname
          );
        }
      } catch (error) {
        // Clean up URL parameters on error
        window.history.replaceState(
          {},
          document.title,
          window.location.pathname
        );
      }
    }
  }, [activateHash, dispatch]);

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [otherTabLocation, setOtherTabLocation] = useState(null);
  const [otherTabMerchant, setOtherTabMerchant] = useState(null);
  const [impersonationModalOpen, setImpersonationModalOpen] = useState(false);
  const [hotkeysModalOpen, setHotkeysModalOpen] = useState(false);
  const [goToStoreUnavailableOpen, setGoToStoreUnavailableOpen] =
    useState(false);
  const fetchTimer = useRef();

  const goToStore = () => {
    if (!selectedMerchant) return;
    if (!selectedMerchant.direct) {
      if (user?.type === "saucey_admin") {
        setGoToStoreUnavailableOpen(true);
      }
      return;
    }
    const store = selectedLocation ?? selectedMerchant;
    window.open(
      store.custom_direct_web_urls?.[0]
        ? `https://${store.custom_direct_web_urls[0]}`
        : `${config.direct_web_prefix}${
            store.direct_web_url ?? store.store_id ?? store.easy_id
          }`,
      "_blank"
    );
  };

  const copyStoreEasyID = () => {
    if (!selectedLocation) return;
    navigator.clipboard
      .writeText(`${selectedLocation.name} (${selectedLocation.store_id})`)
      .then(() => {
        dispatch(
          addToast({
            type: "success",
            message: "Copied StoreID + Name to clipboard",
            timer: 5,
          })
        );
      });
  };
  const copyMerchantEasyID = () => {
    if (!selectedMerchant) return;
    navigator.clipboard
      .writeText(`${selectedMerchant.name} (${selectedMerchant.easy_id})`)
      .then(() => {
        dispatch(
          addToast({
            type: "success",
            message: "Copied EasyID + Name to clipboard",
            timer: 5,
          })
        );
      });
  };
  // Hotkey handler for CTRL+I impersonation modal

  const joyrideHelper = useCallback((node) => {
    if (node) {
      joyrideHelpers.current = node;
      node.reset(true);
      setStepIndex(0);
    }
  }, []);

  useHotkeys("ctrl+t", () => {
    setRun(true);
  });
  useHotkeys(
    "ctrl+i",
    () => {
      if (user?.type === "saucey_admin") {
        setImpersonationModalOpen(true);
      }
    },
    { preventDefault: true }
  );
  // Not plain ctrl+g: Chrome's Gemini panel claims it before the page sees it
  useHotkeys("ctrl+shift+g", goToStore, { preventDefault: true });
  useHotkeys(
    "ctrl+c+m",
    () => {
      if (user?.type === "saucey_admin") {
        copyMerchantEasyID();
      }
    },
    { preventDefault: true }
  );
  useHotkeys(
    "ctrl+c+s",
    () => {
      if (user?.type === "saucey_admin") {
        copyStoreEasyID();
      }
    },
    { preventDefault: true }
  );
  useEffect(() => {
    if (!user || user.type !== "saucey_admin") {
      return;
    }
    dispatch(getDashboardActions());
    const intervalId = setInterval(() => {
      dispatch(getDashboardActions());
    }, 60000);
    return () => clearInterval(intervalId);
  }, [user, dispatch]);

  useEffect(() => {
    if (!otherTabLocation || !selectedLocation) {
      return;
    }
    if (otherTabLocation !== selectedLocation.id) {
      dispatch({
        type: "SET_SELECTED_LOCATION",
        payload: otherTabLocation,
      });
      setOtherTabLocation(null);
      window.location.reload();
    }
  }, [otherTabLocation]);

  useEffect(() => {
    if (!otherTabMerchant) {
      return;
    }
    if (otherTabMerchant !== selectedMerchant.id) {
      dispatch(setSelectedMerchant(otherTabMerchant));
      setOtherTabMerchant(null);
      window.location.reload();
    }
  }, [otherTabMerchant]);

  useEffect(() => {
    window.dashboardBoardcastChannel = new BroadcastChannel(
      config.environment + "_merchant_dashboard"
    );
    window.dashboardBoardcastChannel.onmessage = (event) => {
      if (event.data.type === "locationChange") {
        setOtherTabLocation(event.data.location);
      }
      if (event.data.type === "merchantChange") {
        setOtherTabMerchant(event.data.merchant);
      }
    };

    document.addEventListener("wheel", function (event) {
      if (document.activeElement.type === "number") {
        document.activeElement.blur();
      }
    });

    // Add Ctrl key listener for hotkeys modal
    let ctrlPressed = false;
    let ctrlTimeout = null;

    const handleKeyDown = (event) => {
      if (event.key === "Control" && !ctrlPressed) {
        ctrlPressed = true;
        setHotkeysModalOpen(true);

        // Clear any existing timeout
        if (ctrlTimeout) {
          clearTimeout(ctrlTimeout);
        }

        // Set timeout to hide modal after 3 seconds
        ctrlTimeout = setTimeout(() => {
          setHotkeysModalOpen(false);
        }, 3000);
      }
    };

    const handleKeyUp = (event) => {
      if (event.key === "Control") {
        ctrlPressed = false;
        // Clear timeout when Ctrl is released
        if (ctrlTimeout) {
          clearTimeout(ctrlTimeout);
          ctrlTimeout = null;
        }
        // Hide modal immediately when Ctrl is released
        setHotkeysModalOpen(false);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("keyup", handleKeyUp);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("keyup", handleKeyUp);
      if (ctrlTimeout) {
        clearTimeout(ctrlTimeout);
      }
    };
  }, []);

  const [seenNotifications, setSeenNotifications] = useState([]);
  const [lastNotification, setLastNotification] = useState(null);

  useEffect(() => {
    if (lastNotification) {
      if (seenNotifications.includes(lastNotification?._id)) {
        return;
      }
      setSeenNotifications([...seenNotifications, lastNotification._id]);
      let onClick = null;
      switch (lastNotification.resource_type) {
        case "Order":
          onClick = "/orders?id=" + lastNotification.resource_id;
          break;
      }
      dispatch({
        type: "ADD_TOAST",
        payload: {
          id: lastNotification._id,
          key: lastNotification._id,
          message: lastNotification.message,
          type: lastNotification.state,
          onClick: onClick,
        },
      });
    }
  }, [lastNotification]);
  useEffect(() => {
    if (selectedLocation) {
      // todo use config
      const socket = io(config.socket, {
        transports: ["websocket"],
      });
      socket.on("connect", () => {
        socket.emit("authentication", { "x-location-id": selectedLocation.id });
      });
      socket.on("refresh", () => {
        dispatch(getActiveOrders(true));
      });
      socket.on("new-notification", (data) => {
        setLastNotification(data);
      });
      if (fetchTimer.current) {
        clearInterval(fetchTimer.current);
      }
      dispatch({ type: "RESET_UNSEEN_ORDER_COUNT" });
      dispatch(getActiveOrders(true));
      fetchTimer.current = setInterval(() => {
        dispatch(getActiveOrders());
      }, 90000);
      return () => {
        if (fetchTimer.current) {
          clearInterval(fetchTimer.current);
        }
        socket.disconnect();
      };
    } else {
      return;
    }
  }, [selectedMerchant, selectedLocation]);

  // Fetch onboard steps when selectedLocation is a prospect
  useEffect(() => {
    if (selectedLocation?.prospect) {
      dispatch(fetchOnboardSteps());
    } else {
      // Clear onboard steps if location is not a prospect
      dispatch({ type: "SET_ONBOARD_STEPS", payload: null });
    }
  }, [selectedLocation, dispatch]);

  useEffect(() => {
    if (isAuthenticated) {
      if (user?.type !== "prospect") {
        dispatch(getMerchants());
      }

      // dispatch({ type: "SET_CHANGE_PASSWORD_MODAL_OPEN", payload: true });
    }
  }, [isAuthenticated, user]);

  // Initialize notification settings when user logs in and merchants are loaded
  useEffect(() => {
    if (
      isAuthenticated &&
      user?.id &&
      isDirect &&
      !isApp
    ) {
      Intercom({
        app_id: "k6v36r8e",
        name: user.name,
        email: user.email,
        hide_default_launcher: true,
      });
    } else {
      shutdown();
    }
    if (
      isAuthenticated &&
      user?.id &&
      merchants?.length > 0 &&
      isApp &&
      navigate &&
      dispatch
    ) {
      initializeNotificationSettings(user, merchants, navigate, dispatch);
    }
  }, [
    isAuthenticated,
    user?.id,
    merchants,
    isApp,
    navigate,
    dispatch,
    isDirect,
    config,
  ]);

  useEffect(() => {
    if (!selectedMerchant) {
      return;
    }

    // Load CRM settings when merchant changes
    if (selectedMerchant.mtUrl) {
      dispatch(loadCrmSettings());
    }

    dispatch({
      type: "REMOVE_TOAST",
      payload: "billing",
    });
    if (selectedMerchant.status === "grace") {
      dispatch({
        type: "ADD_TOAST",
        payload: {
          message:
            user.type === "saucey_admin"
              ? "Account is in grace period, please notify merchant to update their billing information"
              : "Your account is in grace period. Please update your billing information to avoid suspension.",
          type: "warning",
          stuck: user.type !== "saucey_admin",
          id: "billing",
        },
      });
    } else if (selectedMerchant.status === "suspended") {
      dispatch({
        type: "ADD_TOAST",
        payload: {
          message:
            user.type === "saucey_admin"
              ? "Account is suspended, please notify merchant to update their billing information"
              : "Your account has been suspended. Please update your billing information to reactivate your account.",
          type: "error",
          stuck: user.type !== "saucey_admin",
          id: "billing",
        },
      });
    }
  }, [selectedMerchant, user]);

  useEffect(() => {
    document.querySelector("html").style.scrollBehavior = "auto";
    window.scroll({ top: 0 });
    document.querySelector("html").style.scrollBehavior = "";
  }, [location.pathname]); // triggered on route change

  useEffect(() => {
    if (isApp) {
      registerNotifications(navigate);
    }
  }, [navigate, isApp]);

  // Initialize SafeArea plugin for mobile app
  useEffect(() => {
    if (isApp) {
      const initializeSafeArea = async () => {
        try {
          // Remove any existing listeners first
          await SafeArea.removeAllListeners();

          // Get initial safe area insets
          const initialInsets = await SafeArea.getSafeAreaInsets();

          // Set initial CSS variables
          const { insets } = initialInsets;
          for (const [key, value] of Object.entries(insets)) {
            document.documentElement.style.setProperty(
              `--safe-area-inset-${key}`,
              `${value}px`
            );
          }

          // Listen for safe area changes
          await SafeArea.addListener("safeAreaChanged", (data) => {
            const { insets } = data;
            for (const [key, value] of Object.entries(insets)) {
              document.documentElement.style.setProperty(
                `--safe-area-inset-${key}`,
                `${value}px`
              );
            }
          });
        } catch (error) {
          console.error("Failed to initialize SafeArea:", error);
        }
      };

      initializeSafeArea();
    }
  }, [isApp]);

  // Initialize Universal Links handling for mobile app
  useEffect(() => {
    if (isApp) {
      const initializeUniversalLinks = async () => {
        try {
          // Handle URL open events (when app is already running)
          const handleUrlOpen = (event) => {
            const url = new URL(event.url);

            if (url.search) {
              navigate(
                {
                  pathname: url.pathname,
                  search: url.search,
                },
                { replace: true }
              );
            } else {
              navigate(
                {
                  pathname: url.pathname,
                },
                { replace: true }
              );
            }
          };
          await CapacitorApp.addListener("appUrlOpen", handleUrlOpen);
        } catch (error) {
          console.error("Failed to initialize Universal Links:", error);
        }
      };

      initializeUniversalLinks();
    }
  }, [isApp, navigate]);

  // Redirect / to /orders when theme is mp — skip while in onboarding so
  // the MarketplaceOnboardingChecklist can render on /.
  useEffect(() => {
    if (
      isMP &&
      location.pathname === "/" &&
      isAuthenticated &&
      selectedMerchant &&
      !selectedLocation?.onboarding
    ) {
      navigate("/orders", { replace: true });
    }
  }, [
    isMP,
    location.pathname,
    isAuthenticated,
    selectedMerchant,
    selectedLocation?.onboarding,
    navigate,
  ]);

  // Listen for token removal in localStorage to logout across all tabs
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === "token" && e.newValue === null && e.oldValue !== null) {
        // Token was removed, logout the user
        dispatch({ type: "LOGOUT" });
      }
    };

    // Listen for storage changes (works across tabs)
    window.addEventListener("storage", handleStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, [dispatch]);

  return (
    <>
      <div className="app-container">
        <div
          className={
            "absolute top-0 left-1/2 -translate-x-1/2 z-[100] text-center toast-container"
          }
        >
          {toasts.map((toast) => {
            return (
              <Toast
                type={toast.type}
                open={true}
                key={toast.id}
                setOpen={(opened) => {
                  if (!opened) {
                    dispatch({ type: "REMOVE_TOAST", payload: toast.id });
                  }
                }}
                stuck={toast.stuck}
              >
                <div
                  onClick={() => {
                    if (toast.onClick) {
                      navigate(toast.onClick);
                    }
                  }}
                  className={toast.onClick ? "cursor-pointer underline" : ""}
                >
                  {toast.message}
                </div>
              </Toast>
            );
          })}
        </div>

        {/* MFA Required Modal - shown regardless of authentication status */}
        <MFARequiredModal
          isOpen={mfaSetupRequired}
          onClose={() =>
            dispatch({ type: "SET_MFA_SETUP_REQUIRED", payload: false })
          }
          onMFAComplete={() => {
            // TODO: Check if MFA is properly set up, then authenticate user
            dispatch({ type: "SET_MFA_SETUP_REQUIRED", payload: false });
            // Set user as fully authenticated now that MFA is set up
            dispatch({ type: "LOGIN_SUCCESS", payload: { user } });
          }}
          dismissible={false}
        />
        {isAuthenticated && !resetToken && <AnnouncementModal />}
        {!isAuthenticated || resetToken ? (
          <Routes>
            {/* <Route path="/signup" element={<Signup />} /> */}
            <Route path="*" element={<Signin />} />
          </Routes>
        ) : !selectedMerchant && user?.type !== "prospect" ? (
          <div className="flex h-[100dvh] items-center justify-center bg-white dark:bg-slate-900">
            {merchantsLoadState === "error" ? (
              <div className="flex flex-col items-center gap-4 text-center px-6 max-w-md">
                <p className="text-lg font-semibold text-slate-800 dark:text-slate-100">
                  Couldn't load your merchants
                </p>
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  The request to the menu service failed. This is usually a
                  connection, VPN, or backend issue — not your account.
                </p>
                <button
                  className="px-4 py-2 rounded-md bg-indigo-500 hover:bg-indigo-600 text-white text-sm font-medium"
                  onClick={() => dispatch(getMerchants())}
                >
                  Retry
                </button>
              </div>
            ) : merchantsLoadState === "empty" ? (
              <div className="flex flex-col items-center gap-4 text-center px-6 max-w-md">
                <p className="text-lg font-semibold text-slate-800 dark:text-slate-100">
                  No merchants on this account
                </p>
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  This account
                  {user?.email ? ` (${user.email})` : ""} isn't associated with
                  any merchants on this environment. If that's unexpected, check
                  you're pointed at the right backend, or log out and back in.
                </p>
                <button
                  className="px-4 py-2 rounded-md bg-slate-200 dark:bg-slate-700 text-slate-800 dark:text-slate-100 text-sm font-medium"
                  onClick={() => dispatch(logout(true))}
                >
                  Log out
                </button>
              </div>
            ) : (
              <InlineLoader message="Loading application..." />
            )}
          </div>
        ) : (
          <div className="flex h-[100dvh] overflow-hidden app-main-container">
            {/* Sidebar */}

            {user?.type !== "prospect" && (
              <>
                <LocationSelector />
                <TosModal />
              </>
            )}

            <Sidebar
              sidebarOpen={sidebarOpen}
              setSidebarOpen={(open) => {
                setStepIndex(stepIndex === 2 ? 3 : stepIndex);
                setSidebarOpen(run && stepIndex >= 2 ? true : open);
              }}
              sidebarExpanded={sidebarExpanded}
              setSidebarExpanded={(expanded) => {
                if (stepIndex === 2) {
                  setStepIndex(3);
                  if (!expanded) {
                    expanded = true;
                  }
                }
                setSidebarExpanded(expanded);
              }}
            />

            {/* Content area */}
            <div
              className="relative flex flex-col flex-1 overflow-y-auto overflow-x-hidden"
              ref={scrollYRef}
            >
              {/*  Site header */}
              <Header
                sidebarOpen={sidebarOpen}
                dropdownOpen={dropdownOpen}
                setDropdownOpen={(open) => {
                  if (stepIndex === 0) {
                    setStepIndex(1);
                  } else if (stepIndex === 1) {
                    setStepIndex(2);
                  }
                  setDropdownOpen(open);
                }}
                setSidebarOpen={(open) => {
                  setStepIndex(stepIndex === 2 ? 3 : stepIndex);
                  setSidebarOpen(open);
                }}
                impersonationModalOpen={impersonationModalOpen}
                setImpersonationModalOpen={setImpersonationModalOpen}
              />
              <main className="grow overflow-y-auto" ref={mainScrollContainerRef}>
                {/*<Tutorial*/}
                {/*  steps={steps}*/}
                {/*  getHelpers={joyrideHelper}*/}
                {/*  callback={handleJoyrideCallback}*/}
                {/*  locale={{*/}
                {/*    nextLabelWithProgress: "Next ({step} of {steps})",*/}
                {/*  }}*/}
                {/*  run={run}*/}
                {/*  showProgress*/}
                {/*  showSkipButton*/}
                {/*  stepIndex={stepIndex}*/}
                {/*/>*/}
                {user?.type !== "prospect" &&
                  (!selectedMerchant ||
                    selectedMerchant.status !== "suspended" ||
                    user.type === "saucey_admin") && (
                    <Routes>
                      <Route
                        exact
                        path="/"
                        element={
                          isMP && selectedLocation?.onboarding ? (
                            <CanView mpOnly isRoute>
                              <MarketplaceOnboardingChecklist />
                            </CanView>
                          ) : (
                            <CanView directOnly isRoute>
                              {user?.type === "prospect" ? <AccountSetup /> : selectedMerchant?.prospect ? <OnboardChecklist /> : <Dashboard />}
                            </CanView>
                          )
                        }
                      />
                      <Route
                        path="/reports"
                        element={
                          <CanView permissions="manager" directOnly isRoute>
                            <Reports />
                          </CanView>
                        }
                      />
                      <Route path="/orders" element={<Orders />} />
                      <Route path="/orders/:id" element={<Orders />} />
                      <Route path="/past-orders" element={<PastOrders />} />
                      <Route path="/past-orders/:id" element={<PastOrders />} />
                      <Route
                        path="/crm/people"
                        element={
                          <CanView directOnly sauceyAdminAlwaysShow isRoute>
                            <Customers />
                          </CanView>
                        }
                      />
                      <Route
                        path="/crm/people/:id"
                        element={<CanView directOnly sauceyAdminAlwaysShow isRoute><CustomerDetail /></CanView>}
                      />
                      <Route path="/crm/campaigns" element={<CanView directOnly isRoute><CrmCampaigns /></CanView>} />
                      <Route
                        path="/crm/broadcasts"
                        element={<CanView directOnly isRoute><CrmBroadcasts /></CanView>}
                      />
                      <Route
                        path="/crm/broadcasts/:id"
                        element={<CanView directOnly isRoute><BroadcastFormWrapper /></CanView>}
                      />
                      <Route path="/crm/settings" element={<CanView permissions="manager" directOnly isRoute><CrmSettings /></CanView>} />
                      <Route
                        path="/crm/campaigns/:id"
                        element={<CanView permissions="manager" directOnly isRoute><CampaignFormWrapper /></CanView>}
                      />
                      <Route
                        path="/catalog/products"
                        element={<CanView   directOnly isRoute><ProductsListing /></CanView>}
                      />
                      <Route
                        path="/catalog/master-menu"
                        element={
                          <CanView permissions="saucey_admin" isRoute>
                            <MasterMenuManagement />
                          </CanView>
                        }
                      />
                      <Route
                        path="/catalog/master-menu/new"
                        element={
                          <CanView permissions="saucey_admin" isRoute>
                            <MasterMenuCreateProduct />
                          </CanView>
                        }
                      />
                      <Route
                        path="/catalog/categories"
                        element={
                          <CanView permissions="manager" isRoute>
                            <Categories />
                          </CanView>
                        }
                      />
                      <Route
                        path="/external-feeds"
                        element={
                          <CanView permissions="manager" isRoute>
                            <ExternalFeedsSettings />
                          </CanView>
                        }
                      />
                      <Route
                        path="/external-feeds/ingestion/:jobId"
                        element={
                          <CanView permissions="manager" isRoute>
                            <IngestionDetail />
                          </CanView>
                        }
                      />
                      <Route
                        path="/external-feeds/:tab"
                        element={
                          <CanView permissions="manager" isRoute>
                            <ExternalFeedsSettings />
                          </CanView>
                        }
                      />
                      <Route
                        path="/clover-callback"
                        element={<CanView directOnly isRoute><CloverCallback /></CanView>}
                      />
                      <Route path="/settings/account" element={<Account />} />

                      {/*saucey admin routes*/}

                      <Route path="/dispatch" element={<Cartwheel />} />
                      <Route
                        path="/admin/admins"
                        element={
                          <CanView permissions="saucey_admin" isRoute>
                            <AdminUsers />
                          </CanView>
                        }
                      />
                      <Route
                        path="/id-scans"
                        element={
                          <CanView permissions="saucey_admin" isRoute>
                            <IDScans />
                          </CanView>
                        }
                      />
                      <Route
                        path="/id-scans/:id"
                        element={
                          <CanView permissions="saucey_admin" isRoute>
                            <IDScans />
                          </CanView>
                        }
                      />
                      <Route
                        path="/admin/sellers"
                        element={
                          <CanView permissions="saucey_admin" isRoute>
                            <AllStores />
                          </CanView>
                        }
                      />
                      <Route
                        path="/admin/merchants"
                        element={
                          <CanView permissions="saucey_admin" isRoute>
                            <Merchants />
                          </CanView>
                        }
                      />

                      <Route
                        path="/admin/sellers"
                        element={
                          <CanView permissions="saucey_admin" isRoute>
                            <AllStores />
                          </CanView>
                        }
                      />
                      <Route
                        path="/admin/state-restrictions"
                        element={
                          <CanView permissions="saucey_admin" isRoute>
                            <StateRestrictions />
                          </CanView>
                        }
                      />
                      <Route
                        path="/admin/menu-cards"
                        element={
                          <CanView permissions="saucey_admin" isRoute>
                            <MainMenuCards />
                          </CanView>
                        }
                      />
                      <Route
                        path="/admin/marketplace-commissions"
                        element={
                          <CanView permissions="saucey_admin" isRoute>
                            <MarketplaceCommissions />
                          </CanView>
                        }
                      />
                      <Route
                        path="/admin/compliance"
                        element={
                          <CanView permissions="saucey_admin" isRoute>
                            <ComplianceLayout />
                          </CanView>
                        }
                      >
                        <Route
                          index
                          element={<Navigate to="tax-rules" replace />}
                        />
                        <Route path="tax-rules" element={<TaxRulesTab />} />
                        <Route
                          path="state-restrictions"
                          element={<ComplianceStateRestrictions />}
                        />
                        <Route
                          path="purchase-attestations"
                          element={<CompliancePurchaseAttestations />}
                        />
                        <Route
                          path="product-tags"
                          element={<ComplianceProductTags />}
                        />
                        <Route
                          path="product-georestriction"
                          element={<ComplianceProductGeorestrictions />}
                        />
                        <Route
                          path="compliance-check"
                          element={<ComplianceCheck />}
                        />
                      </Route>
                      <Route
                        path="/admin/tax-rules"
                        element={
                          <Navigate to="/admin/compliance/tax-rules" replace />
                        }
                      />
                      <Route
                        path="/admin/review-custom-products"
                        element={
                          <CanView permissions="saucey_admin" isRoute>
                            <ReviewCustomProducts />
                          </CanView>
                        }
                      />

                      {/*Manager only routes*/}
                      <Route
                        path="/settings/commissions"
                        element={
                          <CanView permissions="manager" mpOnly isRoute>
                            <CommissionsSettings />
                          </CanView>
                        }
                      />
                      <Route
                        path="/settings/licenses"
                        element={
                          <CanView permissions="manager" mpOnly isRoute>
                            <LicensesSettings />
                          </CanView>
                        }
                      />
                      <Route
                        path="/settings/store/appearance"
                        element={
                          <CanView permissions="manager" directOnly isRoute>
                            <Store />
                          </CanView>
                        }
                      />
                      <Route
                        path="/settings/store/hours"
                        element={
                          <CanView permissions="manager" directOnly isRoute>
                            <Store />
                          </CanView>
                        }
                      />
                      <Route
                        path="/settings/store"
                        element={
                          <CanView permissions="manager" directOnly isRoute>
                            <Store />
                          </CanView>
                        }
                      />
                      <Route
                        path="/settings/hours"
                        element={
                          <CanView permissions="manager" directOnly isRoute>
                            <Store />
                          </CanView>
                        }
                      />
                      <Route
                        path="/settings/business"
                        element={
                          <CanView permissions="manager" directOnly isRoute>
                            <Business />
                          </CanView>
                        }
                      />
                      <Route
                        path="/settings/online-store"
                        element={
                          <CanView permissions="manager" directOnly isRoute>
                            <OnlineStore />
                          </CanView>
                        }
                      />
                      <Route
                        path="/settings/users"
                        element={
                          <CanView permissions="manager" isRoute>
                            <Users />
                          </CanView>
                        }
                      />
                      <Route
                        path="/settings/notifications"
                        element={<Notifications />}
                      />
                      <Route
                        path="/marketing"
                        element={
                          <CanView permissions="manager" isRoute>
                            <Marketing />
                          </CanView>
                        }
                      />
                      <Route
                        path="/settings/fraud-management"
                        element={
                          <CanView permissions="manager" directOnly isRoute>
                            <FraudManagement />
                          </CanView>
                        }
                      />
                      <Route
                        path="/products/new"
                        element={
                          <CanView permissions="manager" directOnly isRoute>
                            <CustomProduct />
                          </CanView>
                        }
                      />
                      <Route
                        path="/products/:sku_id"
                        element={
                          <CanView permissions="manager" directOnly isRoute>
                            <CustomProduct />
                          </CanView>
                        }
                      />

                      {/*store admin routes*/}
                      <Route
                        path="/settings/finance"
                        element={
                          <CanView permissions="admin" directOnly isRoute>
                            <Payouts />
                          </CanView>
                        }
                      />
                      <Route
                        path="/settings/billing"
                        element={
                          <CanView permissions="admin" directOnly isRoute>
                            <Billing />
                          </CanView>
                        }
                      />
                      <Route
                        path="/settings/customization"
                        element={
                          <CanView permissions="admin" directOnly isRoute>
                            <Customization />
                          </CanView>
                        }
                      />

                      <Route
                        path="/settings/mothership"
                        element={
                          <CanView permissions="admin" directOnly isRoute>
                            <Mothership />
                          </CanView>
                        }
                      />
                      {(selectedLocation?.prospect || selectedLocation?.unpublished )&& (
                        <Route
                          path="/onboarding"
                          element={<OnboardChecklist />}
                        />
                      )}
                      {(selectedLocation?.onboarding) && (
                        <Route
                          path="/marketplace-onboarding"
                          element={
                            <CanView mpOnly isRoute>
                              <MarketplaceOnboarding />
                            </CanView>
                          }
                        />
                      )}
                      <Route path="*" element={<PageNotFound />} />
                    </Routes>
                    
                  )}
                {selectedMerchant &&
                  selectedMerchant.status === "suspended" &&
                  user.type !== "saucey_admin" && (
                    <Routes>
                      <Route
                        path="/settings/billing"
                        element={
                          <CanView permissions="admin" isRoute>
                            <Billing />
                          </CanView>
                        }
                      />

                      <Route path="*" element={<Billing />} />
                    </Routes>
                  )}
                {user?.type === "prospect" && (
                  <Routes>
                    <Route path="/" element={<AccountSetup />} />
                    <Route path="/onboarding" element={<AccountSetup />} />
                    <Route path="/settings/account" element={<Account />} />
                    <Route path="*" element={<AccountSetup />} />
                  </Routes>
                )}
              </main>
            </div>
          </div>
        )}
        {/* Hotkeys Modal */}
        <CanView permissions="saucey_admin">
          <HotkeysModal
            modalOpen={
              hotkeysModalOpen &&
              !impersonationModalOpen &&
              !goToStoreUnavailableOpen
            }
            setModalOpen={setHotkeysModalOpen}
            user={user}
          />
        </CanView>

        {/* Demo Environment Modal */}
        <DemoEnvironmentModal
          isOpen={showDemoModal}
          onClose={() =>
            dispatch({ type: "SET_DEMO_MODAL_SHOW", payload: false })
          }
        />

        {/* Prospect Conversion Modal */}
        <ProspectConversionModal isOpen={!!prospect_conversion_id} />

        <ChangePasswordModal />
        <CanView permissions="saucey_admin">
          <ImpersonationModal
            modalOpen={impersonationModalOpen}
            setModalOpen={setImpersonationModalOpen}
          />
          <ModalBasic
            title="Go to store"
            modalOpen={goToStoreUnavailableOpen}
            setModalOpen={setGoToStoreUnavailableOpen}
            width="md"
          >
            <p className="text-sm text-slate-600 dark:text-slate-300">
              This feature is not currently available for marketplace stores.
            </p>
          </ModalBasic>
        </CanView>
      </div>
    </>
  );
}

export default App;
